import React, { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Container, Link, Typography, CssBaseline } from '@mui/material';

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ThemeProvider as CustomThemeProvider, useTheme } from "../contexts/ThemeContext";
import { useSystemButtonStyles } from "../hooks/useSystemButtonStyles";
import AuthPageToggleBar from "../components/AuthPageToggleBar";
import {
  authCardShellSx,
  authLogoSx,
  authPageOuterSx,
} from "../utils/authPageStyles";
import { APP_FONT_FAMILY } from "../constants/typography";
import { getMm, getPageTitleSx } from "../theme/modernMaterialEnterprise";

const FONT = APP_FONT_FAMILY;
const LAST_UPDATED = "2026-10-17";

const SECTION_KEYS = [
  "data_collected",
  "personnummer",
  "how_we_use",
  "permissions",
  "sharing",
  "retention",
  "rights",
  "children",
  "security",
  "changes",
  "contact",
];

function PrivacyPolicyContent() {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { outlineButtonSx } = useSystemButtonStyles();
  const cardShellSx = useMemo(() => authCardShellSx(isDarkMode), [isDarkMode]);
  const logoSx = useMemo(() => authLogoSx(isDarkMode), [isDarkMode]);

  const headingSx = {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: { xs: "1.05rem", sm: "1.125rem" },
    color: getMm(isDarkMode).onSurface,
    mt: 3,
    mb: 1,
  };

  const bodySx = {
    fontFamily: FONT,
    fontSize: "0.875rem",
    lineHeight: 1.65,
    color: getMm(isDarkMode).onSurfaceVariant,
    whiteSpace: "pre-line",
  };

  const listSx = {
    ...bodySx,
    pl: 2.5,
    my: 1,
    "& li": { mb: 0.75 },
  };

  return (
    <Box sx={{ ...authPageOuterSx(isDarkMode), minHeight: "100vh" }}>
      <CssBaseline />
      <AuthPageToggleBar />
      <Container maxWidth="md" sx={{ py: { xs: 3, sm: 5 }, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box
            component="img"
            src={isDarkMode ? "NewEraLogo.png" : "NewEraBlackLogo.png"}
            alt="NewEra"
            sx={{ ...logoSx, maxWidth: { xs: 200, sm: 260 }, mb: 2 }}
          />
          <Typography
            component="h1"
            sx={{ ...getPageTitleSx(isDarkMode), mb: 0.75, textAlign: "center" }}
          >
            {t("privacy_page_title")}
          </Typography>
          <Typography sx={{ ...bodySx, textAlign: "center" }}>
            {t("privacy_page_subtitle")}
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT,
              fontSize: "0.8125rem",
              color: getMm(isDarkMode).onSurfaceVariant,
              mt: 1,
            }}
          >
            {t("privacy_page_last_updated", { date: LAST_UPDATED })}
          </Typography>
        </Box>

        <Box sx={{ ...cardShellSx, p: { xs: 2.5, sm: 4 }, mb: 3 }}>
          <Typography sx={bodySx}>{t("privacy_intro")}</Typography>

          {SECTION_KEYS.map((key) => (
            <Box key={key} component="section">
              <Typography component="h2" sx={{ ...headingSx, mt: 3 }}>
                {t(`privacy_section_${key}_title`)}
              </Typography>
              <Typography sx={bodySx}>{t(`privacy_section_${key}_body`)}</Typography>
              {key === "data_collected" ? (
                <Box component="ul" sx={listSx}>
                  {(t("privacy_data_collected_items", { returnObjects: true }) || []).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </Box>
              ) : null}
              {key === "permissions" ? (
                <Box component="ul" sx={listSx}>
                  {(t("privacy_permissions_items", { returnObjects: true }) || []).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </Box>
              ) : null}
              {key === "sharing" ? (
                <Box component="ul" sx={listSx}>
                  {(t("privacy_sharing_items", { returnObjects: true }) || []).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </Box>
              ) : null}
              {key === "rights" ? (
                <Box component="ul" sx={listSx}>
                  {(t("privacy_rights_items", { returnObjects: true }) || []).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </Box>
              ) : null}
            </Box>
          ))}

          <Typography sx={{ ...bodySx, mt: 3, fontSize: "0.8125rem", color: getMm(isDarkMode).onSurfaceVariant }}>
            {t("privacy_google_play_note")}{" "}
            <Link
              href="https://support.google.com/googleplay/android-developer/topic/9877467"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: isDarkMode ? "#86EFAC" : "#388E3C" }}
            >
              Google Play — Privacy, Deception and Device Abuse
            </Link>
            . {t("data_deletion_privacy_link_prefix")}{" "}
            <Link component={RouterLink} to="/data-deletion" sx={{ color: isDarkMode ? "#86EFAC" : "#388E3C" }}>
              {t("login_data_deletion")}
            </Link>
          </Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
          <Button
            component={RouterLink}
            to="/login"
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{ ...outlineButtonSx, textTransform: "none" }}
          >
            {t("privacy_back_to_login")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export default function PrivacyPolicyPage() {
  return (
    <CustomThemeProvider>
      <PrivacyPolicyContent />
    </CustomThemeProvider>
  );
}
